/* ==========================================================================
   Werewolf Food - Predictive search modal
   - Opens from any [data-search-open] trigger, Cmd+K / Ctrl+K, or "/"
   - Live results via Shopify's predictive search API (/search/suggest.json)
   - Popular-search chips, quick links, recent searches, keyboard navigation
   ========================================================================== */
(() => {
  'use strict';

  const dialog = document.querySelector('[data-search-modal]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const form = dialog.querySelector('[data-search-form]');
  const input = dialog.querySelector('[data-search-input]');
  const clearBtn = dialog.querySelector('[data-search-clear]');
  const idleState = dialog.querySelector('[data-search-idle]');
  const loadingState = dialog.querySelector('[data-search-loading]');
  const resultsState = dialog.querySelector('[data-search-results]');
  const emptyState = dialog.querySelector('[data-search-empty]');
  const emptyTerm = dialog.querySelector('[data-search-empty-term]');
  const footer = dialog.querySelector('[data-search-footer]');
  const allLink = dialog.querySelector('[data-search-all]');
  const allTerm = dialog.querySelector('[data-search-all-term]');
  const status = dialog.querySelector('[data-search-status]');
  const recentWrap = dialog.querySelector('[data-search-recent]');
  const recentList = dialog.querySelector('[data-search-recent-list]');
  const recentClear = dialog.querySelector('[data-search-recent-clear]');

  const endpoint = dialog.dataset.endpoint || '/search/suggest.json';
  const searchUrl = dialog.dataset.searchUrl || '/search';
  const moneyFormat = dialog.dataset.moneyFormat || '\u00a3{{amount}}';
  const limit = parseInt(dialog.dataset.limit, 10) || 6;

  const RECENT_KEY = 'wf_recent_searches';
  const RECENT_MAX = 6;
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 180;

  let controller = null;
  let debounceTimer = null;
  let lastQuery = '';
  let activeIndex = -1;
  let lastFocused = null;

  /* ---- Helpers ---------------------------------------------------------- */

  const escapeHtml = (str) =>
    String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlight = (text, query) => {
    const safe = escapeHtml(text);
    const words = query
      .trim()
      .split(/\s+/)
      .filter((w) => w.length >= 2)
      .map(escapeRegExp);
    if (!words.length) return safe;
    const re = new RegExp('(' + words.join('|') + ')', 'gi');
    return safe.replace(re, '<mark>$1</mark>');
  };

  const formatMoney = (amount) => {
    const value = typeof amount === 'string' ? parseFloat(amount) : Number(amount || 0);
    if (!isFinite(value)) return '';
    const cents = Math.round(value * 100);
    const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const withDelims = (num, precision, thousands, decimal) => {
      const fixed = (num / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    };
    let out;
    switch (key) {
      case 'amount_no_decimals':
        out = withDelims(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = withDelims(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withDelims(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = withDelims(cents, 2, "'", '.');
        break;
      default:
        out = withDelims(cents, 2, ',', '.');
    }
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]*>/g, '');
  };

  const imageUrl = (url, width) => {
    if (!url) return '';
    const clean = url.split('?')[0];
    return clean + '?width=' + width;
  };

  // Mirrors snippets/product-card.liquid - "15KG - Chicken [Grain Free] - Adult"
  // becomes chips (15KG, Grain Free) + "Chicken (middle dot) Adult".
  const parseTitle = (raw) => {
    let title = raw || '';
    let attr = '';
    let size = '';
    const bracket = title.match(/\[([^\]]+)\]/);
    if (bracket) {
      attr = bracket[1].trim();
      title = title.replace(bracket[0], '');
    }
    const parts = title.split(' - ').map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      const first = parts[0];
      const up = first.toUpperCase();
      const isWeight = up.includes('KG') || (first.length <= 5 && up.endsWith('G'));
      if (isWeight) {
        size = first;
        title = parts.slice(1).join(' \u00b7 ');
      }
    }
    return { title: title.trim(), attr, size };
  };

  const setStatus = (msg) => {
    if (status) status.textContent = msg;
  };

  const show = (state) => {
    [idleState, loadingState, resultsState, emptyState].forEach((el) => {
      if (!el) return;
      el.classList.toggle('is-visible', el === state);
    });
  };

  /* ---- Recent searches -------------------------------------------------- */

  const getRecent = () => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string') : [];
    } catch (e) {
      return [];
    }
  };

  const saveRecent = (term) => {
    const t = (term || '').trim();
    if (!t) return;
    try {
      const list = [t].concat(getRecent().filter((s) => s.toLowerCase() !== t.toLowerCase())).slice(0, RECENT_MAX);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode - ignore */
    }
  };

  const renderRecent = () => {
    if (!recentWrap || !recentList) return;
    const list = getRecent();
    if (!list.length) {
      recentWrap.hidden = true;
      recentList.innerHTML = '';
      return;
    }
    recentWrap.hidden = false;
    recentList.innerHTML = list
      .map(
        (term) =>
          '<button type="button" class="wf-chip wf-chip--recent" data-search-term="' +
          escapeHtml(term) +
          '">' +
          escapeHtml(term) +
          '</button>'
      )
      .join('');
  };

  if (recentClear) {
    recentClear.addEventListener('click', () => {
      try {
        localStorage.removeItem(RECENT_KEY);
      } catch (e) {
        /* ignore */
      }
      renderRecent();
      input.focus();
    });
  }

  /* ---- Rendering -------------------------------------------------------- */

  const arrowSvg =
    '<span class="wf-result__arrow" aria-hidden="true"><svg viewBox="0 0 16 16" fill="none"><path d="M2 8h11M9 3.5 13.5 8 9 12.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

  const renderProduct = (p, q, i) => {
    const parsed = parseTitle(p.title);
    const img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
    const onSale = p.compare_at_price_max && parseFloat(p.compare_at_price_max) > parseFloat(p.price_max || p.price);
    const varies = p.price_min != null && p.price_max != null && parseFloat(p.price_min) !== parseFloat(p.price_max);
    const chips = [];
    if (parsed.size) chips.push('<span class="wf-result__chip">' + escapeHtml(parsed.size) + '</span>');
    if (parsed.attr) chips.push('<span class="wf-result__chip wf-result__chip--attr">' + escapeHtml(parsed.attr) + '</span>');
    if (p.available === false) chips.push('<span class="wf-result__chip wf-result__chip--soldout">Sold out</span>');

    return (
      '<li class="wf-search__item" style="--i:' + i + '">' +
      '<a href="' + escapeHtml(p.url) + '" class="wf-result" data-search-item role="option" aria-selected="false">' +
      '<span class="wf-result__media">' +
      (img
        ? '<img src="' + escapeHtml(imageUrl(img, 160)) + '" alt="" loading="lazy" width="56" height="56">'
        : '\uD83D\uDC3E') +
      '</span>' +
      '<span class="wf-result__body">' +
      (chips.length ? '<span class="wf-result__chips">' + chips.join('') + '</span>' : '') +
      '<span class="wf-result__title">' + highlight(parsed.title, q) + '</span>' +
      (p.type ? '<span class="wf-result__meta">' + escapeHtml(p.type) + '</span>' : '') +
      '</span>' +
      '<span class="wf-result__price">' +
      (varies ? '<span class="wf-result__price-from">from</span>' : '') +
      (onSale ? '<s>' + escapeHtml(formatMoney(p.compare_at_price_max)) + '</s>' : '') +
      '<span>' + escapeHtml(formatMoney(varies ? p.price_min : p.price)) + '</span>' +
      '</span>' +
      arrowSvg +
      '</a></li>'
    );
  };

  const renderCompact = (item, q, i, emoji, kicker) => {
    const img = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
    return (
      '<li class="wf-search__item" style="--i:' + i + '">' +
      '<a href="' + escapeHtml(item.url) + '" class="wf-result wf-result--compact" data-search-item role="option" aria-selected="false">' +
      '<span class="wf-result__media' + (img ? '' : ' wf-result__media--round') + '">' +
      (img ? '<img src="' + escapeHtml(imageUrl(img, 120)) + '" alt="" loading="lazy" width="40" height="40">' : emoji) +
      '</span>' +
      '<span class="wf-result__body">' +
      '<span class="wf-result__title">' + highlight(item.title, q) + '</span>' +
      '<span class="wf-result__meta">' + escapeHtml(kicker) + '</span>' +
      '</span>' +
      arrowSvg +
      '</a></li>'
    );
  };

  const section = (label, emoji, inner) =>
    '<div class="wf-search__section"><p class="wf-search__label"><span><span class="wf-search__label-emoji">' +
    emoji +
    '</span>' +
    label +
    '</span></p></div>' +
    inner;

  const render = (data, q) => {
    const r = (data && data.resources && data.resources.results) || {};
    const products = r.products || [];
    const collections = r.collections || [];
    const articles = r.articles || [];
    const pages = r.pages || [];
    const queries = r.queries || [];
    const total = products.length + collections.length + articles.length + pages.length;

    if (!total) {
      if (emptyTerm) emptyTerm.textContent = q;
      show(emptyState);
      footer.hidden = false;
      setStatus('No results for ' + q);
      return;
    }

    let html = '';

    if (queries.length) {
      html +=
        '<div class="wf-search__col wf-search__col--full"><div class="wf-search__section"><div class="wf-search__chips">' +
        queries
          .slice(0, 5)
          .map(
            (s) =>
              '<button type="button" class="wf-chip wf-chip--suggest" data-search-term="' +
              escapeHtml(s.text) +
              '">' +
              escapeHtml(s.text) +
              '</button>'
          )
          .join('') +
        '</div></div></div>';
    }

    let i = 0;
    const hasSecondary = collections.length || articles.length || pages.length;

    if (products.length) {
      html +=
        '<div class="wf-search__col' + (hasSecondary ? '' : ' wf-search__col--full') + '">' +
        section('Products', '\uD83C\uDF56', '<ul class="wf-search__list" role="listbox" aria-label="Products">' +
          products.map((p) => renderProduct(p, q, i++)).join('') +
          '</ul>') +
        '</div>';
    }

    if (hasSecondary) {
      let secondary = '';
      if (collections.length) {
        secondary += section('Collections', '\uD83D\uDC36', '<ul class="wf-search__list" role="listbox" aria-label="Collections">' +
          collections.slice(0, 4).map((c) => renderCompact(c, q, i++, '\uD83D\uDC36', 'Collection')).join('') +
          '</ul>');
      }
      if (articles.length) {
        secondary += section('Guides & stories', '\uD83D\uDCDD', '<ul class="wf-search__list" role="listbox" aria-label="Articles">' +
          articles.slice(0, 3).map((a) => renderCompact(a, q, i++, '\uD83D\uDCDD', 'Blog')).join('') +
          '</ul>');
      }
      if (pages.length) {
        secondary += section('Help & info', '\uD83D\uDCAC', '<ul class="wf-search__list" role="listbox" aria-label="Pages">' +
          pages.slice(0, 3).map((p) => renderCompact(p, q, i++, '\uD83D\uDCAC', 'Page')).join('') +
          '</ul>');
      }
      html += '<div class="wf-search__col' + (products.length ? '' : ' wf-search__col--full') + '">' + secondary + '</div>';
    }

    resultsState.innerHTML = html;
    show(resultsState);
    footer.hidden = false;
    setStatus(total + (total === 1 ? ' result' : ' results') + ' for ' + q + '. Use up and down arrows to browse.');
  };

  /* ---- Fetching --------------------------------------------------------- */

  const buildUrl = (q) => {
    const params = new URLSearchParams();
    params.set('q', q);
    params.set('resources[type]', 'product,collection,article,page,query');
    params.set('resources[limit]', String(limit));
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,body');
    return endpoint + '?' + params.toString();
  };

  const search = (q) => {
    if (controller) controller.abort();
    controller = new AbortController();
    show(loadingState);
    footer.hidden = true;
    setStatus('Searching...');

    fetch(buildUrl(q), {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Predictive search failed: ' + res.status);
        return res.json();
      })
      .then((data) => {
        if (q !== lastQuery) return; // stale
        render(data, q);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        // API unavailable - let the form submit do the work instead.
        if (emptyTerm) emptyTerm.textContent = q;
        show(emptyState);
        footer.hidden = false;
        setStatus('Press enter to search for ' + q);
      });
  };

  const updateAllLink = (q) => {
    if (allTerm) allTerm.textContent = q;
    if (allLink) allLink.href = searchUrl + '?q=' + encodeURIComponent(q);
  };

  const onInput = () => {
    const q = input.value.trim();
    dialog.classList.toggle('has-query', q.length > 0);
    activeIndex = -1;
    clearActive();
    updateAllLink(q);
    lastQuery = q;
    clearTimeout(debounceTimer);

    if (q.length < MIN_CHARS) {
      if (controller) controller.abort();
      show(idleState);
      footer.hidden = true;
      setStatus('');
      return;
    }
    debounceTimer = setTimeout(() => search(q), DEBOUNCE_MS);
  };

  input.addEventListener('input', onInput);

  /* ---- Keyboard navigation -------------------------------------------- */

  const items = () => {
    const list = Array.from(resultsState.querySelectorAll('[data-search-item]'));
    if (!footer.hidden && allLink && resultsState.classList.contains('is-visible')) list.push(allLink);
    return list;
  };

  const clearActive = () => {
    dialog.querySelectorAll('.is-active').forEach((el) => {
      el.classList.remove('is-active');
      el.setAttribute('aria-selected', 'false');
    });
    input.removeAttribute('aria-activedescendant');
  };

  const setActive = (idx) => {
    const list = items();
    if (!list.length) return;
    clearActive();
    activeIndex = ((idx % list.length) + list.length) % list.length;
    const el = list[activeIndex];
    el.classList.add('is-active');
    el.setAttribute('aria-selected', 'true');
    if (!el.id) el.id = 'wf-search-opt-' + activeIndex;
    input.setAttribute('aria-activedescendant', el.id);
    el.scrollIntoView({ block: 'nearest' });
  };

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(activeIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(activeIndex - 1);
    } else if (e.key === 'Enter' && activeIndex > -1) {
      const el = items()[activeIndex];
      if (el && el.href) {
        e.preventDefault();
        saveRecent(input.value);
        window.location.href = el.href;
      }
    }
  });

  /* ---- Delegated clicks inside the modal ------------------------------ */

  dialog.addEventListener('click', (e) => {
    const termBtn = e.target.closest('[data-search-term]');
    if (termBtn) {
      input.value = termBtn.getAttribute('data-search-term') || '';
      input.focus();
      onInput();
      return;
    }
    if (e.target.closest('[data-search-item], [data-search-all]')) {
      saveRecent(input.value);
      return;
    }
    // Click on the dimmed area outside the panel closes
    if (e.target === dialog) close();
  });

  form.addEventListener('submit', (e) => {
    const q = input.value.trim();
    if (!q) {
      e.preventDefault();
      input.focus();
      return;
    }
    saveRecent(q);
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      input.value = '';
      onInput();
      input.focus();
    });
  }

  /* ---- Open / close --------------------------------------------------- */

  const open = () => {
    if (dialog.open) {
      input.focus();
      return;
    }
    lastFocused = document.activeElement;
    // Close the mobile nav drawer if it's open so we don't stack modals
    document.querySelectorAll('dialog[data-mobile-nav][open]').forEach((d) => d.close());
    renderRecent();
    dialog.showModal();
    requestAnimationFrame(() => {
      input.focus();
      input.select();
    });
    document.dispatchEvent(new CustomEvent('wf:search:opened'));
  };

  const close = () => {
    if (!dialog.open) return;
    dialog.close();
  };

  dialog.addEventListener('close', () => {
    if (controller) controller.abort();
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('wf:search:closed'));
  });

  dialog.querySelectorAll('[data-search-close]').forEach((btn) => btn.addEventListener('click', close));

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-search-open]');
    if (!trigger) return;
    e.preventDefault();
    open();
  });

  const isTyping = (el) => {
    if (!el) return false;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  };

  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      dialog.open ? close() : open();
      return;
    }
    if (e.key === '/' && !dialog.open && !isTyping(document.activeElement) && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      open();
    }
  });

  // Expose a tiny API for other scripts / theme editor
  window.WFSearch = { open, close };

  // Shopify theme editor: keep the modal usable when the header re-renders
  document.addEventListener('shopify:section:load', () => {
    if (dialog.open) close();
  });
})();
